<template>
  <tr>
    <td
      style="word-break: break-word; font-size: 0px"
      align="center"
      :style="{ padding: padding }"
    >
      <table
        role="presentation"
        cellpadding="0"
        cellspacing="0"
        style="border-collapse: separate"
        :align="block.attributes['button-align']"
        :width="width"
        border="0"
      >
        <tbody>
          <tr>
            <td
              :style="{ ...tdStyle, ...hoverTdStyle }"
              :align="block.attributes['button-align']"
              valign="middle"
              @mouseover="isHovered = true"
              @mouseleave="isHovered = false"
              @click.stop="openButtonOptions"
            >
              <div
                style="max-width: 100%"
                :style="buttonStyle"
                :href="block.attributes['href']"
                target="_blank"
                @click.stop="
                  openTiny();
                  openButtonOptions();
                "
              >
                <div v-if="block.mode === 'dynamic'" :style="editorStyle">
                  {{ buttonContent }}
                </div>
                <inline-text-editor
                  v-else
                  ref="editor"
                  v-model="buttonContent"
                  :product-size="true"
                  :editor-style="editorStyle"
                  :disable-image="true"
                  :toolbar="[
                    `bold italic underline strikethrough charmap removeformat code nonbreaking${
                      generalStore.isRtlLanguage ? ' ltr rtl' : ''
                    }`,
                  ]"
                  :inline="true"
                  @onBlur="getButtonContentIfEmpty"
                ></inline-text-editor>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import InlineTextEditor from "../elements/InlineTextEditor.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { useI18n } from "vue-i18n";
  import { StyleValue, computed, ref } from "vue";

  const props = defineProps({
    block: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  });

  const emits = defineEmits(["open-settings"]);

  const mjmlStore = useMjmlStore();
  const generalStore = useGeneralStore();

  const isHovered = ref(false);

  const buttonContent = computed({
    get() {
      return props.item["button"];
    },
    set(val: string) {
      const block = props.block;
      const sync = block.syncButtonText ?? true;

      if (!sync) {
        mjmlStore.updateBlockItemAttribute({
          prop: "button",
          value: val,
          product: true,
          prodstructure: prodstructure.value,
          itemIndex: props.index,
        });
        return;
      }

      const applyToAll =
        !!block.blockName &&
        !!mjmlStore.mjml.feedBlocksApplyToAll[block.blockName];

      if (applyToAll) {
        mjmlStore.updateAllSiblingFeedBlockItemsAttribute({
          blockName: block.blockName,
          prop: "button",
          value: val,
          prodstructure: prodstructure.value,
        });
        return;
      }

      mjmlStore.throttledTakeSnapshot();
      const items = mjmlStore.getActiveBlockFeedItems(block);
      items?.forEach((_: unknown, index: number) => {
        mjmlStore.updateBlockItemAttribute({
          prop: "button",
          value: val,
          product: true,
          prodstructure: prodstructure.value,
          itemIndex: index,
          skipSnapshot: true,
        });
      });
    },
  });

  const prodstructure = computed(() => {
    if (props.block.attributes["prodstructure"]) {
      return props.block.attributes["prodstructure"];
    }

    return false;
  });

  const editorStyle = computed<StyleValue>(() => {
    return {
      color:
        isHovered.value && hoverColor.value ? hoverColor.value : color.value,
      fontSize: fontSize.value,
      textAlign: textAlign.value,
    };
  });

  const buttonStyle = computed<StyleValue>(() => {
    return {
      margin: innerPadding.value,
      color:
        isHovered.value && hoverColor.value ? hoverColor.value : color.value,
      display: "inline-block",
      textDecoration: textDecoration.value,
      lineHeight: "100%",
      fontSize: fontSize.value,
      fontFamily: fontFamily.value,
      fontWeight: fontWeigth.value,
      textTransform: textTransform.value,
      fontStyle: fontStyle.value,
    };
  });

  const bgColor = computed(() => {
    return props.block.attributes["button-background-color"]
      ? props.block.attributes["button-background-color"]
      : "#414141";
  });

  const hoverBgColor = computed(() => {
    return props.block["button-hover"]?.["background-color"];
  });

  const gradient = computed(() => {
    return props.block.attributes["button-gradient"]
      ? props.block.attributes["button-gradient"]
      : "";
  });

  const hoverGradient = computed(() => {
    return props.block["button-hover"]?.gradient;
  });

  const color = computed(() => {
    return props.block.attributes["button-color"]
      ? props.block.attributes["button-color"]
      : "#ffffff";
  });

  const hoverColor = computed(() => {
    return props.block["button-hover"]?.color;
  });

  const padding = computed(() => {
    return props.block.attributes["button-padding"]
      ? props.block.attributes["button-padding"]
      : "10px 25px 10px 25px";
  });

  const innerPadding = computed(() => {
    const pad = props.block.attributes["button-inner-padding"];
    if (pad) {
      return pad;
    } else {
      return "10px 25px 10px 25px";
    }
  });

  const fontSize = computed(() => {
    return props.block.attributes["button-font-size"]
      ? props.block.attributes["button-font-size"]
      : "13px";
  });

  const textDecoration = computed(() => {
    return props.block.attributes["button-text-decoration"]
      ? props.block.attributes["button-text-decoration"]
      : "none";
  });

  const textTransform = computed(() => {
    return props.block.attributes["button-text-transform"]
      ? props.block.attributes["button-text-transform"]
      : "none";
  });

  const fontStyle = computed(() => {
    return props.block.attributes["button-font-style"]
      ? props.block.attributes["button-font-style"]
      : "normal";
  });

  const fontWeigth = computed(() => {
    return props.block.attributes["button-font-weight"]
      ? props.block.attributes["button-font-weight"]
      : "normal";
  });

  const fontFamily = computed(() => {
    return props.block.attributes["button-font-family"]
      ? props.block.attributes["button-font-family"]
      : "Helvetica, Arial, sans-serif";
  });

  const tdStyle = computed(() => {
    return {
      backgroundColor: bgColor.value,
      border: "none",
      "border-radius": borderRadius.value,
      backgroundImage: gradient.value,
    };
  });

  const hoverTdStyle = computed(() => {
    if (!isHovered.value) {
      return {};
    }
    return {
      backgroundColor: hoverBgColor.value ? hoverBgColor.value : bgColor.value,
      backgroundImage: hoverGradient.value
        ? hoverGradient.value
        : gradient.value,
    };
  });

  const width = computed(() => {
    return props.block.attributes["button-width"]
      ? props.block.attributes["button-width"]
      : "auto";
  });

  const textAlign = computed(() => {
    return props.block.attributes["button-text-align"] || "center";
  });

  const borderRadius = computed(() => {
    if (props.block.attributes["button-border-radius"] == "none") {
      return 0;
    } else {
      return props.block.attributes["button-border-radius"];
    }
  });

  const editor = ref(null);

  const openTiny = () => {
    if (props.block.mode === "dynamic") {
      return;
    }
    //@ts-expect-error
    editor.value.focus();
  };

  const openButtonOptions = () => {
    emits("open-settings", "button");

    let timeoutTime = 400;
    if (
      document.querySelector(
        '[data-testid="product-description-tab"] [data-testid="t-tab-toggle-open"] button'
      )
    ) {
      timeoutTime = 10;
    }

    setTimeout(() => {
      const openSetting: HTMLElement = document.querySelector(
        '[data-testid="product-button-tab"] [data-testid="t-tab-toggle-open"] button'
      ) as HTMLElement;

      if (openSetting && !openSetting.classList.contains("!outline")) {
        openSetting.click();
      }

      if (openSetting) {
        setTimeout(() => {
          (document.querySelector(".scroll-area") as HTMLElement).scrollTop =
            openSetting.offsetTop - openSetting.offsetHeight;
        }, 100);
      }
    }, timeoutTime);
  };

  const { t } = useI18n();

  const getButtonContentIfEmpty = () => {
    if (buttonContent.value === "") {
      mjmlStore.updateBlockAttribute({
        prop: "button",
        value: t("product.more"),
        product: true,
        prodstructure: prodstructure.value,
      });
    }
  };
</script>
